import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import dayjs from 'dayjs';
import { HIDE_ORDER_HOURS } from 'constants/new-order-status';
import {
  isAfterHours,
  isAllocatingStatus,
  isCancelledStatus,
  isCompletedStatus,
  isFailedStatus,
  isPendingScheduleStatus,
  isReturnedStatus,
} from 'common/utils/status';
import styles from './eta.less';

const Eta = ({
  time,
  schedule,
  endTime,
  description,
  status,
  hideCashlessPOD,
}) => {
  const timeText = useMemo(
    () => {
      if (isAllocatingStatus(status)) {
        return I18n.t('statusCard.eta.allocating');
      }

      if (isPendingScheduleStatus(status)) {
        if (!schedule) {
          return '';
        }

        const { from, to } = schedule;
        const [fromInMs, toInMs] = [from * 1000, to * 1000];

        const fromDayjs = dayjs(fromInMs);
        const toDayjs = dayjs(toInMs);

        if (fromDayjs.isToday()) {
          return I18n.t('statusCard.eta.scheduleToday', {
            time: `${fromDayjs.format('h:mm')} - ${toDayjs.format('h:mmA')}`,
          });
        }

        if (fromDayjs.isTomorrow()) {
          return I18n.t('statusCard.eta.scheduleTomorrow', {
            time: `${fromDayjs.format('h:mm')} - ${toDayjs.format('h:mmA')}`,
          });
        }

        return `${fromDayjs.format('ddd, D MMM, h:mmA')}`;
      }

      if (isFailedStatus(status)) {
        return I18n.t('statusCard.eta.returning');
      }

      if (isReturnedStatus(status)) {
        return I18n.t('statusCard.eta.returned');
      }

      if (isCompletedStatus(status)) {
        return isAfterHours(endTime, HIDE_ORDER_HOURS)
          ? I18n.t('statusCard.eta.deliveryCompleted')
          : I18n.t('statusCard.eta.passCompleted', {
              time: dayjs(endTime).format('h:mm A'),
            });
      }

      if (isCancelledStatus(status)) {
        return I18n.t('statusCard.eta.deliveryCancelled');
      }

      const eta = dayjs(time);
      return eta.format('h:mm A');
    },
    [time, status, schedule],
  );

  const descriptionText = useMemo(
    () => {
      const formattedEndTime = dayjs(endTime).format('DD MMM YYYY, hh:mm A');
      if (isAllocatingStatus(status)) {
        return I18n.t('statusCard.description.allocating');
      }

      if (isPendingScheduleStatus(status)) {
        return I18n.t('statusCard.description.placed');
      }

      if (isFailedStatus(status)) {
        return I18n.t(
          hideCashlessPOD
            ? 'statusCard.description.returning'
            : 'statusCard.description.resumeDelivery',
        );
      }

      if (isCompletedStatus(status)) {
        return isAfterHours(endTime, HIDE_ORDER_HOURS)
          ? formattedEndTime
          : I18n.t('statusCard.eta.thanksUsingExpress');
      }

      if (isCancelledStatus(status) || isReturnedStatus(status)) {
        return I18n.t('statusCard.eta.on', { time: formattedEndTime });
      }

      return description;
    },
    [status, endTime, description],
  );

  return (
    <div className={styles.etaContainer}>
      <p className={styles.eta}>{timeText}</p>
      {descriptionText && (
        <p className={styles.etaDescription}>{descriptionText}</p>
      )}
    </div>
  );
};

Eta.propTypes = {
  time: PropTypes.number.isRequired,
  schedule: PropTypes.shape({
    from: PropTypes.number,
    to: PropTypes.number,
  }),
  endTime: PropTypes.number,
  description: PropTypes.string,
  status: PropTypes.string,
  hideCashlessPOD: PropTypes.bool,
};

Eta.defaultProps = {
  schedule: null,
  endTime: 0,
  description: '',
  status: '',
  hideCashlessPOD: false,
};

export default Eta;
